/**
 * VerifyPage — PUBLIC, no-login record verifier (June 2026).
 *
 * External validators (hospital procurement, legal review, auditors) paste the
 * SHA-256 content hash printed on an exported Clinician Rounds hand-off and get
 * an independent Verified ✓ / Not Found answer with the block's position in the
 * append-only, tamper-evident audit ledger. Zero PHI; hits the public
 * GET /api/clinician/rounds/verify endpoint. No authentication required.
 */
import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ShieldCheck, ShieldX, Search, Boxes, Link2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEV_DOT = {
  CRITICAL: 'bg-red-500', HIGH: 'bg-orange-500', MODERATE: 'bg-amber-500', LOW: 'bg-slate-500',
};

export default function VerifyPage() {
  const [hash, setHash] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [autoRan, setAutoRan] = useState(false);

  const runVerify = useCallback(async (raw) => {
    const h = (raw || '').trim().toLowerCase();
    if (!h) return;
    setBusy(true); setResult(null);
    try {
      const res = await axios.get(`${API}/clinician/rounds/verify`, { params: { hash: h } });
      setResult(res.data);
    } catch (e) {
      setResult({ verified: false, reason: 'lookup_error' });
    } finally {
      setBusy(false);
    }
  }, []);

  const verify = useCallback(() => runVerify(hash), [hash, runVerify]);

  // QR / deep-link entry: a hand-off PDF QR points at /verify?hash=<sha256>, which
  // auto-fills and auto-runs the check so a single scan returns Verified / Not-found.
  useEffect(() => {
    const qh = new URLSearchParams(window.location.search).get('hash');
    if (qh) {
      setHash(qh);
      setAutoRan(true);
      runVerify(qh);
    }
  }, [runVerify]);

  return (
    <div className="min-h-screen bg-slate-900" data-testid="verify-page">
      <div className="mx-auto max-w-2xl px-6 py-16">
        <div className="flex items-center gap-3">
          <Boxes className="h-8 w-8 text-teal-400" />
          <h1 className="text-2xl font-bold text-white sm:text-3xl">Verify a Record</h1>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-slate-300">
          Paste the SHA-256 hash printed on a P.Ai Clinician Rounds hand-off to independently
          confirm it is sealed in our append-only, tamper-evident audit ledger. This page is public —
          no login, and it never reveals any patient information.
        </p>

        {autoRan && (
          <div
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-teal-500/40 bg-teal-500/10 px-3 py-1.5 text-xs font-semibold text-teal-300"
            data-testid="verify-autorun-banner"
          >
            <Search className="h-3.5 w-3.5" /> Hash loaded from scan — verifying automatically
          </div>
        )}

        <div className="mt-8 rounded-2xl border border-slate-700 bg-slate-800 p-6 shadow-2xl">
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-400">
            SHA-256 content hash
          </label>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Input
              value={hash}
              onChange={(e) => setHash(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && verify()}
              placeholder="64 hexadecimal characters"
              className="flex-1 bg-slate-900 font-mono text-sm text-white"
              data-testid="verify-hash-input"
            />
            <Button onClick={verify} disabled={busy} className="bg-teal-600 hover:bg-teal-700" data-testid="verify-submit">
              <Search className="mr-2 h-4 w-4" /> {busy ? 'Checking\u2026' : 'Verify'}
            </Button>
          </div>

          {result && (
            <div className="mt-6" data-testid="verify-result">
              {result.verified ? (
                <div className="rounded-xl border border-emerald-500 bg-emerald-500/10 p-5" data-testid="verify-status-verified">
                  <div className="flex items-center gap-2 text-emerald-400">
                    <ShieldCheck className="h-6 w-6" />
                    <span className="text-lg font-bold">Verified</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-200">
                    This record is sealed in the P.Ai audit ledger as{' '}
                    <strong className="text-white">
                      block {result.blockPosition ?? '?'} of {result.chainLength ?? '?'}
                    </strong>.
                  </p>
                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-lg bg-slate-900/60 p-3">
                      <p className="text-xs text-slate-400">Sealed</p>
                      <p className="text-slate-100">{result.sealedAt ? new Date(result.sealedAt).toLocaleString() : '\u2014'}</p>
                    </div>
                    <div className="rounded-lg bg-slate-900/60 p-3">
                      <p className="text-xs text-slate-400">Overall acuity</p>
                      <p className="flex items-center gap-2 text-slate-100">
                        <span className={`h-2.5 w-2.5 rounded-full ${SEV_DOT[(result.severity || 'LOW').toUpperCase()] || 'bg-slate-500'}`} />
                        {result.severity || '\u2014'}
                      </p>
                    </div>
                    <div className="rounded-lg bg-slate-900/60 p-3">
                      <p className="text-xs text-slate-400">Findings reconciled</p>
                      <p className="text-slate-100">{result.flagCount ?? '\u2014'}</p>
                    </div>
                    <div className="rounded-lg bg-slate-900/60 p-3">
                      <p className="flex items-center gap-1 text-xs text-slate-400"><Link2 className="h-3 w-3" /> Links to</p>
                      <p className="truncate font-mono text-xs text-slate-300" title={result.previousHash}>{result.previousHash || '\u2014'}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-red-500 bg-red-500/10 p-5" data-testid="verify-status-failed">
                  <div className="flex items-center gap-2 text-red-400">
                    <ShieldX className="h-6 w-6" />
                    <span className="text-lg font-bold">
                      {result.reason === 'invalid_hash_format' ? 'Invalid hash' : 'Not found'}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-slate-200">
                    {result.reason === 'invalid_hash_format'
                      ? 'That does not look like a SHA-256 hash (it must be 64 hexadecimal characters).'
                      : 'No sealed record matches this hash. If it came from a P.Ai hand-off, the document may have been altered or the hash mistyped.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-slate-500">
          Records are sealed with a binding hash-chain (each block hash =
          SHA-256(previous&nbsp;hash&nbsp;+&nbsp;content)), so altering any past record breaks every
          record after it.
        </p>
      </div>
    </div>
  );
}
